import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    CircularProgress
} from '@mui/material';

/**
 * CorrectionModal - модалка ввода причины корректировки заказа.
 *
 * Props:
 *   open: boolean                          - открыта ли модалка
 *   onClose: () => void                    - закрыть (Cancel / overlay click)
 *   orderName: string                      - номер заказа для Title
 *   onSubmit: (reason: string) => void | Promise<void>
 *                                          - отправить причину (trimmed).
 *                                            При Promise модалка ждёт resolve,
 *                                            на reject показывает ошибку.
 *
 * Поведение:
 *   - Сброс reason/loading/error при каждом открытии.
 *   - Submit дизейблит кнопку пока пусто или идёт запрос.
 *   - После успешного onSubmit модалка НЕ закрывается автоматически -
 *     родитель сам вызывает onClose (обычно после navigate).
 */
export default function CorrectionModal({ open, onClose, orderName, onSubmit }) {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setReason('');
            setLoading(false);
            setError(null);
        }
    }, [open]);

    const handleSubmit = async () => {
        const trimmed = reason.trim();
        if (!trimmed || loading) return;
        setLoading(true);
        setError(null);
        try {
            await onSubmit(trimmed);
        } catch (err) {
            setError(
                (err?.response?.data?.error) ||
                (err?.message) ||
                'Ошибка отправки'
            );
        } finally {
            setLoading(false);
        }
    };

    const canSubmit = reason.trim().length > 0 && !loading;

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={loading}
        >
            <DialogTitle>Корректировка заказа {orderName}</DialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    multiline
                    rows={4}
                    fullWidth
                    label="Причина корректировки"
                    placeholder="Опишите проблему (например: не хватает 50г масла Роза)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={loading}
                />
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={loading}
                    color="inherit"
                >
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    Отправить
                </Button>
            </DialogActions>
        </Dialog>
    );
}
